/**
 * OEM Sticky Add-to-Cart controller (OpenEye Marketing).
 * - Shows the bar as soon as the main buy button scrolls up past `data-offset`
 *   (much sooner than Horizon's "fully scrolled past" trigger).
 * - Inline size/variant selector resolves to a real variant (price + availability).
 * - Adds the selected variant directly via the shared cart-add helper.
 * - Optional quick-add "you may also like" tray with per-item size + add.
 */
import { addVariant } from '@theme/oem-cart-add';

class OEMStickyATC extends HTMLElement {
  #variants = [];
  #current = null;
  #buyBtnBlock = null;
  #ticking = false;
  #recsLoaded = false;

  connectedCallback() {
    try {
      const raw = this.querySelector('[data-oem-variants]');
      this.#variants = raw ? JSON.parse(raw.textContent) : [];
    } catch (e) {
      this.#variants = [];
    }

    this.#buyBtnBlock =
      document.querySelector('.buy-buttons-block') ||
      document.querySelector('product-form-component') ||
      document.querySelector('[ref="addToCartButton"]');

    this.#resolveFromSelects();

    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);

    this.#onScroll();
    window.addEventListener('scroll', this.#queueScroll, { passive: true });
    window.addEventListener('resize', this.#queueScroll, { passive: true });

    // Keep the bar's selection in sync if the shopper uses the main variant picker.
    document.addEventListener('variant:selected', this.#onExternalVariant);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#queueScroll);
    window.removeEventListener('resize', this.#queueScroll);
    document.removeEventListener('variant:selected', this.#onExternalVariant);
  }

  get bar() {
    return this.querySelector('.oem-sticky__bar');
  }

  /* ---------- visibility ---------- */

  #queueScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#ticking = false;
      this.#onScroll();
    });
  };

  #onScroll() {
    const bar = this.bar;
    if (!bar || !this.#buyBtnBlock) return;

    const offset = parseInt(this.dataset.offset || '240', 10);
    const scrollFallback = parseInt(this.dataset.scrollFallback || '600', 10);
    const rect = this.#buyBtnBlock.getBoundingClientRect();

    // Near the bottom of the page, don't cover the footer / recommendations CTA.
    const nearBottom =
      document.documentElement.scrollHeight - (window.scrollY + window.innerHeight) < 120;

    // Trigger A: buy button's bottom edge rose into the top `offset` band.
    // Trigger B: scrolled a fixed distance — needed on desktop, where the
    // product-details column is position:sticky so the buy button never
    // actually leaves the viewport and trigger A can't fire.
    const buttonScrolledPast = rect.bottom < offset;
    const scrolledFar = window.scrollY > scrollFallback;
    const shouldShow = (buttonScrolledPast || scrolledFar) && !nearBottom;

    if (this.hidden) this.hidden = false;
    bar.dataset.shown = shouldShow ? 'true' : 'false';

    // Publish the bar's real height so the clock can spring exactly clear of it
    // (the bar is two rows on mobile, so a fixed offset isn't enough).
    if (shouldShow) {
      document.documentElement.style.setProperty('--oem-sticky-h', `${bar.offsetHeight}px`);
    }

    // The tray is a separate fixed element — close it whenever the bar hides,
    // or it stays floating over the page after scrolling back up.
    if (!shouldShow) this.#closeRecsTray();
  }

  #closeRecsTray() {
    const tray = this.querySelector('[data-oem-recs-tray]');
    if (!tray || tray.hidden) return;
    tray.hidden = true;
    const toggle = this.querySelector('[data-oem-recs-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  /* ---------- variant resolution ---------- */

  #resolveFromSelects() {
    const selects = [...this.querySelectorAll('[data-oem-option-index]')];
    let variant;

    if (selects.length === 0) {
      // Single default variant.
      const id = this.dataset.defaultVariant;
      variant = this.#variants.find((v) => String(v.id) === String(id)) || this.#variants[0];
    } else {
      const chosen = selects
        .sort((a, b) => Number(a.dataset.oemOptionIndex) - Number(b.dataset.oemOptionIndex))
        .map((s) => s.value);
      variant = this.#variants.find(
        (v) => v.options.length === chosen.length && v.options.every((o, i) => o === chosen[i])
      );
    }

    this.#current = variant || null;
    this.#applyVariant();
  }

  #applyVariant() {
    const priceEl = this.querySelector('[data-oem-price]');
    const addBtn = this.querySelector('[data-oem-add]');
    const label = this.querySelector('[data-oem-add-label]');
    const v = this.#current;

    if (v && priceEl) priceEl.textContent = this.#money(v.price);

    if (addBtn) {
      const available = Boolean(v && v.available);
      addBtn.disabled = !available;
      if (label) {
        label.textContent = !v
          ? 'Unavailable'
          : available
            ? label.dataset.addText || label.textContent || 'Add to cart'
            : 'Sold out';
      }
    }
  }

  #onExternalVariant = (event) => {
    const id = event?.detail?.variant?.id || event?.detail?.id;
    if (!id) return;
    const v = this.#variants.find((x) => String(x.id) === String(id));
    if (!v) return;
    // Reflect option values into the bar's selects.
    this.querySelectorAll('[data-oem-option-index]').forEach((sel) => {
      const idx = Number(sel.dataset.oemOptionIndex);
      if (v.options[idx] != null) sel.value = v.options[idx];
    });
    this.#current = v;
    this.#applyVariant();
  };

  /* ---------- interaction ---------- */

  #onChange = (event) => {
    if (event.target.matches('[data-oem-option-index]')) {
      this.#resolveFromSelects();
    }
  };

  #onClick = (event) => {
    const addBtn = event.target.closest('[data-oem-add]');
    if (addBtn) {
      this.#addCurrent(addBtn);
      return;
    }
    const recBtn = event.target.closest('[data-oem-rec-add]');
    if (recBtn) {
      this.#addRec(recBtn);
      return;
    }
    const toggle = event.target.closest('[data-oem-recs-toggle]');
    if (toggle) {
      this.#toggleRecs(toggle);
    }
  };

  async #addCurrent(btn) {
    if (!this.#current || !this.#current.available) return;
    btn.disabled = true;
    await addVariant(this.#current.id, 1, `oem-sticky-${this.#current.id}`);
    btn.disabled = false;
  }

  /* ---------- recommendations tray ---------- */

  async #toggleRecs(toggle) {
    const tray = this.querySelector('[data-oem-recs-tray]');
    if (!tray) return;
    const open = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!open));
    tray.hidden = open;
    if (!open && !this.#recsLoaded) {
      this.#recsLoaded = true;
      await this.#loadRecs(tray);
    }
  }

  async #loadRecs(tray) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    let products = [];
    try {
      const res = await fetch(
        `${root}recommendations/products.json?product_id=${this.dataset.productId}&limit=6&intent=related`
      );
      if (res.ok) products = (await res.json()).products || [];
    } catch (e) {
      /* silent — recs are a bonus */
    }

    if (!products.length) {
      tray.innerHTML = '<p style="opacity:.6;font-size:.8rem;margin:0">No recommendations right now.</p>';
      return;
    }

    tray.innerHTML = '';
    for (const p of products) {
      if (!p.available) continue;
      const card = document.createElement('div');
      card.className = 'oem-sticky__rec';

      const img = p.featured_image
        ? `<img src="${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=200" alt="" loading="lazy">`
        : '';

      const multi = p.variants && p.variants.length > 1;
      const options = multi
        ? `<select data-oem-rec-select>${p.variants
            .map(
              (v) =>
                `<option value="${v.id}"${v.available ? '' : ' disabled'}>${this.#escape(v.title)}</option>`
            )
            .join('')}</select>`
        : '';
      const firstVariant = (p.variants && p.variants.find((v) => v.available)) || (p.variants && p.variants[0]);

      card.innerHTML =
        `${img}<span class="oem-sticky__rec-title"></span>` +
        `<span style="font-size:.72rem;opacity:.7">${this.#money(p.price)}</span>` +
        `<div class="oem-sticky__rec-row">${options}` +
        `<button type="button" class="button" data-oem-rec-add data-variant-id="${firstVariant ? firstVariant.id : ''}">Add</button></div>`;
      card.querySelector('.oem-sticky__rec-title').textContent = p.title;
      tray.appendChild(card);
    }
  }

  async #addRec(btn) {
    const card = btn.closest('.oem-sticky__rec');
    const select = card && card.querySelector('[data-oem-rec-select]');
    const variantId = select ? select.value : btn.dataset.variantId;
    if (!variantId) return;
    btn.disabled = true;
    const ok = await addVariant(variantId, 1, `oem-sticky-rec-${variantId}`);
    btn.textContent = ok ? 'Added' : 'Add';
    setTimeout(() => {
      btn.textContent = 'Add';
      btn.disabled = false;
    }, 1200);
  }

  /* ---------- utils ---------- */

  #money(cents) {
    const fmt = this.dataset.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const noDec = Math.round(cents / 100).toString();
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDec)
      .replace(/<[^>]*>/g, '');
  }

  #escape(str) {
    const d = document.createElement('div');
    d.textContent = str == null ? '' : String(str);
    return d.innerHTML;
  }
}

if (!customElements.get('oem-sticky-atc')) {
  customElements.define('oem-sticky-atc', OEMStickyATC);
}
